/**
 * Period-end transport: recurring journals, FX revaluation, and the year-end
 * close. Three things a company does when it shuts a period, none of which had
 * a way in until now.
 *
 * Contract facts the screens are built on:
 *   • A recurring template's code and currency are fixed at creation; the
 *     update ignores them. Its lines are REPLACED whole on update.
 *   • Materializing is idempotent per period: a second run reports
 *     `created: false` rather than drafting the same journal twice.
 *   • A re-run of FX revaluation does not overwrite: it reverses run N and
 *     writes run N+1, so what the company believed at close stays readable.
 *   • Amounts are signed the way a journal line is signed — positive is a
 *     debit — which is why one rule ("positive delta is a gain") holds for
 *     receivables, payables and bank balances alike.
 */
import type { components } from "@erp/api-client";

import { apiFetch, type Page } from "@/lib/api/http";

type Schemas = components["schemas"];

export type RecurringJournal = Schemas["RecurringJournal"];
export type RecurringJournalWrite = Schemas["RecurringJournalWrite"];
export type RecurringJournalLine = Schemas["RecurringJournalLine"];
export type RecurringMaterialization = Schemas["RecurringMaterialization"];
export type FxRevaluationRun = Schemas["FxRevaluationRun"];
export type FxRevaluationLine = Schemas["FxRevaluationLine"];
export type FxRevaluationScope = Schemas["FxRevaluationScope"];
export type YearEndClose = Schemas["YearEndClose"];

// ── Recurring journals ───────────────────────────────────────────────────

export function listRecurringJournals(signal?: AbortSignal): Promise<Page<RecurringJournal>> {
  return apiFetch<Page<RecurringJournal>>("/recurring-journals", {
    query: { limit: 200 },
    signal,
  });
}

export function getRecurringJournal(
  recurringJournalId: string,
  signal?: AbortSignal,
): Promise<RecurringJournal> {
  return apiFetch<RecurringJournal>(
    `/recurring-journals/${encodeURIComponent(recurringJournalId)}`,
    { signal },
  );
}

export function createRecurringJournal(
  body: RecurringJournalWrite,
  idempotencyKey: string,
): Promise<RecurringJournal> {
  return apiFetch<RecurringJournal>("/recurring-journals", {
    method: "POST",
    body,
    idempotencyKey,
  });
}

/** The update replaces the template's lines whole and ignores `code` and
 *  `currency`; a revision is a new set of lines, not a patch of the old. */
export function updateRecurringJournal(
  recurringJournalId: string,
  body: RecurringJournalWrite,
): Promise<RecurringJournal> {
  return apiFetch<RecurringJournal>(
    `/recurring-journals/${encodeURIComponent(recurringJournalId)}`,
    { method: "PUT", body },
  );
}

/** Draft the journals due in a period. Answers one materialization per
 *  template, each saying whether this call is what created it. */
export function materializeRecurringJournals(
  body: { fiscal_period_id: string; recurring_journal_id?: string },
  idempotencyKey: string,
): Promise<{ items: readonly RecurringMaterialization[] }> {
  return apiFetch<{ items: readonly RecurringMaterialization[] }>(
    "/recurring-journal-materializations",
    { method: "POST", body, idempotencyKey },
  );
}

// ── FX revaluation ───────────────────────────────────────────────────────

export function listFxRevaluations(signal?: AbortSignal): Promise<Page<FxRevaluationRun>> {
  return apiFetch<Page<FxRevaluationRun>>("/fx-revaluations", { query: { limit: 100 }, signal });
}

export function getFxRevaluation(
  fxRevaluationRunId: string,
  signal?: AbortSignal,
): Promise<FxRevaluationRun> {
  return apiFetch<FxRevaluationRun>(`/fx-revaluations/${encodeURIComponent(fxRevaluationRunId)}`, {
    signal,
  });
}

export function runFxRevaluation(
  fiscalPeriodId: string,
  idempotencyKey: string,
): Promise<FxRevaluationRun> {
  return apiFetch<FxRevaluationRun>("/fx-revaluations", {
    method: "POST",
    body: { fiscal_period_id: fiscalPeriodId },
    idempotencyKey,
  });
}

// ── Year-end close ───────────────────────────────────────────────────────

export function getYearEndClose(fiscalYearId: string, signal?: AbortSignal): Promise<YearEndClose> {
  return apiFetch<YearEndClose>(
    `/fiscal-years/${encodeURIComponent(fiscalYearId)}/year-end-close`,
    { signal },
  );
}

/** Sweep a year's profit and loss into retained earnings. The request names
 *  the year and nothing else: which period it closes into, and which equity
 *  account receives it, are the server's to decide. */
export function executeYearEndClose(
  fiscalYearId: string,
  idempotencyKey: string,
): Promise<YearEndClose> {
  return apiFetch<YearEndClose>("/year-end-closes", {
    method: "POST",
    body: { fiscal_year_id: fiscalYearId },
    idempotencyKey,
  });
}
